import { ImgHTMLAttributes } from 'react';

const WIDTHS = [384, 640, 750, 828, 1080, 1200, 1920];

function vercelImageUrl(src: string, width: number, quality: number): string {
  return `/_vercel/image?url=${encodeURIComponent(src)}&w=${width}&q=${quality}`;
}

/**
 * Resolves a src to an absolute path suitable for Vercel Image Optimization.
 * Imported assets (from Vite) come through as "/assets/..." after build,
 * root-relative paths like "/lovable-uploads/..." stay as-is,
 * and external URLs (https://...) pass through unchanged.
 */
function resolveSource(src: string): string {
  return src;
}

interface OptimizedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'srcSet'> {
  src: string;
  /** Largest rendered width in CSS pixels. Controls srcset generation. Default 1200. */
  width?: number;
  /** Vercel image quality 1-100. Default 75. */
  quality?: number;
}

export function OptimizedImage({
  src,
  width = 1200,
  quality = 75,
  alt = '',
  loading = 'lazy',
  decoding = 'async',
  ...rest
}: OptimizedImageProps) {
  const resolved = resolveSource(src);
  const isDev = import.meta.env.DEV;

  // In dev mode, Vercel's image endpoint isn't available
  if (isDev) {
    return <img src={resolved} alt={alt} loading={loading} decoding={decoding} {...rest} />;
  }

  // Filter srcset widths to those <= 2x the rendered width
  const maxSrcset = width * 2;
  const srcsetWidths = WIDTHS.filter((w) => w <= maxSrcset);
  if (srcsetWidths.length === 0) srcsetWidths.push(WIDTHS[0]);

  const srcSet = srcsetWidths
    .map((w) => `${vercelImageUrl(resolved, w, quality)} ${w}w`)
    .join(', ');

  const sizes = `(max-width: ${width}px) 100vw, ${width}px`;

  return (
    <img
      src={vercelImageUrl(resolved, srcsetWidths[srcsetWidths.length - 1], quality)}
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      loading={loading}
      decoding={decoding}
      {...rest}
    />
  );
}
